import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { Sentry } from '@shared/app/scripts/services/sentry';

/**
 * @ngdoc service
 * @name posApp.payPlazaRequests
 * @description
 * Service for sending requests to v4 of the PayPlaza payment gateway.
 * The payPlaza ECR webservice v4 documentation is located at
 * http://help.softwear.nl/display/SWUI/PayPlaza+Documentation
 */
angular
  .module('posApp')
  .factory(
    'payPlazaRequests',
    function (
      $http: ng.IHttpService,
      $q: ng.IQService,
      endpoint: any,
      settingsManager: SettingsManager,
      WebSocket: any,
      sentry: Sentry,
    ) {
      const payPlazaRequests = this;

      // return a base url to talk to with pay plaza
      function getUrl() {
        const pinKey = settingsManager.settings.pinId;

        return endpoint.getPayPlazaV4Endpoint() + '?ecrId=' + pinKey;
      }

      // It's possible that we randomly recieve reject responses from PayPlaza.
      // If this happens we want them to be treated as errors.
      function checkForReject(response) {
        if (response.data.rejectMessage) {
          return $q.reject('Reject message received');
        } else {
          return response.data;
        }
      }

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#connect
       * @methodOf posApp.payPlazaRequests
       * @param {promise} cancelPromise
       * a promise that will break the request if it's resolved
       * @description
       * Sends a connect Pin message to pay plaza
       * @return {promise}
       * http request promise
       */
      payPlazaRequests.connect = (cancelPromise) =>
        $http
          .post(
            getUrl(),
            {
              connectRequestMessage: {
                ecrId: settingsManager.settings.pinId,
              },
            },
            {
              timeout: cancelPromise,
            },
          )
          .then((response) => checkForReject(response));

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#wakeNFC
       * @methodOf posApp.payPlazaRequests
       * @param {string} address
       * The ip address of the terminal
       * @param {string} port
       * The port of the terminal
       * @description
       * Sends a websocket connect to wake the device. According to PayPlaza we only
       * need to send a blind request to the terminal via websocket protocol to wake up
       * the device. The response will be blocked by the browser due to CORS issues, but according to them
       * the connection will still be successful and that is all that is needed to wake
       * the NFC reader on the terminal.
       * @return {Void}
       * nothing
       */
      payPlazaRequests.wakeNFC = (address, port) => {
        try {
          const socket = new WebSocket('wss://' + address + ':' + port);

          // 'solution' based on
          // https://stackoverflow.com/questions/25779831/how-to-catch-websocket-connection-to-ws-xxxnn-failed-connection-closed-be
          //
          // In current browsers, the red connection error is still logged. As this
          // is a valid way of addressing errors, hopefully the red log will disappear one day.
          socket.onerror = angular.noop;
        } catch (err) {
          // This should be executed on browsers missing WebSocket
          sentry.captureException(err);
        }
      };

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#poll
       * @methodOf posApp.payPlazaRequests
       * @param {promise} cancelPromise
       * a promise that will break the request if it's resolved
       * @description
       * Gets message from pay plaza
       * @return {promise}
       * http request promise
       */
      payPlazaRequests.poll = (cancelPromise) =>
        $http
          .get(getUrl(), {
            timeout: cancelPromise,
          })
          .then((response) => checkForReject(response));

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#disconnect
       * @methodOf posApp.payPlazaRequests
       * @param {promise} cancelPromise
       * a promise that will break the request if it's resolved
       * @description
       * Sends a disconnect Pin message to pay plaza
       * @return {promise}
       * http request promise
       */
      payPlazaRequests.disconnect = (cancelPromise) =>
        $http
          .post(
            getUrl(),
            {
              disconnectMessage: {
                ecrId: settingsManager.settings.pinId,
              },
            },
            {
              timeout: cancelPromise,
            },
          )
          .then((response) => checkForReject(response));

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#sendTransactionStartMessage
       * @methodOf posApp.payPlazaRequests
       * @param {String} merchRef
       * reference saved on the transaction to match entities in both systems
       * @param {number} amount
       * Transaction amount
       * @param {promise} cancelPromise
       * a promise that will break the request if it's resolved
       * @description
       * Sends a transaction response message to pay plaza
       * @return {promise}
       * http request promise
       */
      payPlazaRequests.sendTransactionStartMessage = (
        merchRef,
        amount,
        cancelPromise,
      ) => {
        let type = '';
        var units = '000';

        if (amount >= 0) {
          type = 'PURCHASE';
          units = amount.toFixed(2).replace('.', '');
        } else {
          type = 'REFUND';
          units = (-amount).toFixed(2).replace('.', '');
        }

        var units;

        return $http
          .post(
            getUrl(),
            {
              transactionStartMessage: {
                type: type,
                orderRef: merchRef,
                amount: {
                  units: units,
                  currencyCode: 'EUR',
                },
              },
            },
            {
              timeout: cancelPromise,
            },
          )
          .then((response) => checkForReject(response));
      };

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#sendPrintSuccess
       * @methodOf posApp.payPlazaRequests
       * @param {promise} cancelPromise
       * a promise that will break the request if it's resolved
       * @description
       * Sends a print success message to pay plaza
       * @return {promise}
       * http request promise
       */
      payPlazaRequests.sendPrintSuccess = (cancelPromise) =>
        $http
          .post(
            getUrl(),
            {
              printResultMessage: {
                resultCode: 'SUCCESS',
              },
            },
            {
              timeout: cancelPromise,
            },
          )
          .then((response) => checkForReject(response));

      /**
       * @ngdoc
       * @name posApp.payPlazaRequests#requestReprint
       * @methodOf posApp.payPlazaRequests
       * @param {promise} cancelPromise
       * a promise that will break the request if it's resolved
       * @description
       * Sends a request for reprint to pay plaza
       * @return {promise}
       * http request promise
       */
      payPlazaRequests.requestReprint = (cancelPromise) =>
        $http
          .post(
            getUrl(),
            {
              printRequestMessage: {
                ecrId: settingsManager.settings.pinId,
              },
            },
            {
              timeout: cancelPromise,
            },
          )
          .then((response) => checkForReject(response));

      return payPlazaRequests;
    },
  );
